/**
 * Per-channel presence of a tracker, derived from the last frame seen on each
 * radio channel. Pure functions: the callers feed timestamps (last position per
 * source, live STOMP frames) and a clock, the UI renders the result.
 *
 * Why three states and not "online": the tracker's ONLINE/OFFLINE status is an
 * MQTT last-will pushed by the 4G firmware or by the mobile app on its behalf,
 * indistinguishable in the database, and LoRa never publishes one. See vault note
 * 2026-09-10 "connectivité tracker".
 */
export type TrackerChannel = '4G' | 'BLE' | 'LORA';
export type ChannelState = 'live' | 'seen' | 'never';

export interface ChannelPresence {
  channel: TrackerChannel;
  state: ChannelState;
  /** Last frame on this channel, undefined when never seen. */
  lastSeen?: Date;
  /** LoRa only: 0–15 raw rssi/snr nibble from the gateway frame. */
  signal?: number;
  /** LoRa only: raw battery byte from the gateway frame. */
  battery?: number;
}

export const TRACKER_CHANNELS: readonly TrackerChannel[] = ['4G', 'BLE', 'LORA'];

/** "live" window per channel, in ms. LoRa is slow by design (slotted, minutes). */
export const LIVE_THRESHOLDS_MS: Record<TrackerChannel, number> = {
  '4G': 2 * 60_000,
  BLE: 1 * 60_000,
  LORA: 10 * 60_000,
};

/** Maps a position `source` (4G, ALGO, BLE, LORA, _4G…) to a channel. ALGO rides the 4G radio. */
export function channelOfSource(source: string | null | undefined): TrackerChannel | undefined {
  const s = (source ?? '').toUpperCase().replace(/^_/, '');
  if (s === '4G' || s === 'ALGO') return '4G';
  if (s === 'BLE') return 'BLE';
  if (s === 'LORA') return 'LORA';
  return undefined;
}

export interface ChannelExtras {
  signal?: number;
  battery?: number;
}

/**
 * Builds the three presences, always in the same order, from the last-seen map.
 * `now` is injected so the "live → seen" decay can be recomputed on a timer.
 */
export function computeChannelPresence(
  lastSeen: Partial<Record<TrackerChannel, Date | undefined>>,
  now: Date,
  extras: Partial<Record<TrackerChannel, ChannelExtras>> = {},
  thresholds: Record<TrackerChannel, number> = LIVE_THRESHOLDS_MS
): ChannelPresence[] {
  return TRACKER_CHANNELS.map(channel => {
    const seen = lastSeen[channel];
    const extra = extras[channel] ?? {};
    if (!seen || isNaN(seen.getTime())) {
      return { channel, state: 'never', ...extra };
    }
    // A negative age means the server clock is ahead of the browser (seconds of skew):
    // the frame just arrived, it is live, not "seen".
    const age = now.getTime() - seen.getTime();
    const state: ChannelState = age < thresholds[channel] ? 'live' : 'seen';
    return { channel, state, lastSeen: seen, ...extra };
  });
}

/** Keeps the most recent timestamp per channel when merging several sources of truth. */
export function mergeLastSeen(
  base: Partial<Record<TrackerChannel, Date | undefined>>,
  channel: TrackerChannel | undefined,
  at: Date | undefined
): Partial<Record<TrackerChannel, Date | undefined>> {
  if (!channel || !at || isNaN(at.getTime())) return base;
  const current = base[channel];
  if (current && current.getTime() >= at.getTime()) return base;
  return { ...base, [channel]: at };
}

/** LoRa signal nibble (0–15) to 0–4 bars for the chip. */
export function loraSignalBars(signal: number | undefined): number {
  if (signal == null || isNaN(signal)) return 0;
  return Math.max(0, Math.min(4, Math.ceil((signal / 15) * 4)));
}

/** Clamps a last-seen date to `now` so relative labels never read "in a few seconds". */
export function clampToNow(date: Date, now: Date = new Date()): Date {
  return date.getTime() > now.getTime() ? now : date;
}

/**
 * Minimal shape of the backend `Tracker.connectivity` (api-tracker 3.23.0). Declared structurally
 * so this util stays free of the generated client and testable in isolation; the page passes
 * `tracker.connectivity` straight in.
 */
export interface ConnectivityChannelInput {
  channel: string;
  state?: ChannelState;
  lastSeen?: string | Date | null;
  signal?: number | null;
  battery?: number | null;
}
export interface ConnectivityInput {
  channels?: ConnectivityChannelInput[] | null;
}

/**
 * Seeds the last-seen and extras maps from the backend connectivity, the base the live merges
 * (STOMP position, `/lora/info`, slot) then refine. The backend already applied the SAME
 * thresholds (see ChannelPresenceRules), so a `live`/`seen` state carries a real `lastSeen` and
 * `computeChannelPresence` recomputes the decay on the front timer without contradicting it.
 */
export function seedFromConnectivity(connectivity: ConnectivityInput | null | undefined): {
  lastSeen: Partial<Record<TrackerChannel, Date>>;
  extras: Partial<Record<TrackerChannel, ChannelExtras>>;
} {
  const lastSeen: Partial<Record<TrackerChannel, Date>> = {};
  const extras: Partial<Record<TrackerChannel, ChannelExtras>> = {};
  for (const entry of connectivity?.channels ?? []) {
    const channel = channelOfSource(entry.channel);
    if (!channel) continue;
    if (entry.lastSeen != null) {
      const date = entry.lastSeen instanceof Date ? entry.lastSeen : new Date(entry.lastSeen);
      if (!isNaN(date.getTime())) lastSeen[channel] = date;
    }
    const extra: ChannelExtras = {};
    if (entry.signal != null) extra.signal = entry.signal;
    if (entry.battery != null) extra.battery = entry.battery;
    if (extra.signal != null || extra.battery != null) extras[channel] = extra;
  }
  return { lastSeen, extras };
}

/** Position datée portant sa source (4G, ALGO, BLE, LORA…), REST ou STOMP `/position`. */
export interface SourcedTimestamp {
  source?: string | null;
  timestamp?: string | number | Date | null;
}

/**
 * Base = connectivité persistée par le backend, raffinée par les positions connues (dernière
 * position REST, position live STOMP) : pour chaque canal on garde la trame la plus récente.
 * Pur : l'appelant passe ensuite le résultat à {@link computeChannelPresence}.
 */
export function channelLastSeenFrom(
  connectivity: ConnectivityInput | null | undefined,
  positions: readonly (SourcedTimestamp | null | undefined)[]
): {
  lastSeen: Partial<Record<TrackerChannel, Date | undefined>>;
  extras: Partial<Record<TrackerChannel, ChannelExtras>>;
} {
  const seed = seedFromConnectivity(connectivity);
  let lastSeen: Partial<Record<TrackerChannel, Date | undefined>> = { ...seed.lastSeen };
  for (const p of positions) {
    if (p?.timestamp == null) continue;
    const at = p.timestamp instanceof Date ? p.timestamp : new Date(p.timestamp);
    lastSeen = mergeLastSeen(lastSeen, channelOfSource(p.source), at);
  }
  return { lastSeen, extras: seed.extras };
}
